import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { History, FileSpreadsheet, User, Clock, AlertCircle } from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import { api, formatApiError } from "@/lib/api";
import { TEST_IDS } from "@/testIds";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ImportHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/import/history");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load history", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PageShell
      testId={TEST_IDS.importHistoryPage}
      kicker="Audit trail"
      title="Import History"
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-2xl">
        Every successful Excel import is recorded with the user, timestamp, file
        name and per-sheet counts. Use this as an audit trail when reconciling
        with source-of-truth data.
      </p>

      {loading && <div className="text-sm text-muted-light">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="cf-card p-10 md:p-14 flex flex-col items-center text-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-ink flex items-center justify-center">
            <History size={22} className="text-gold-500" />
          </div>
          <div className="font-display text-xl text-ink" style={{ fontWeight: 800 }}>
            No imports yet
          </div>
          <p className="text-sm text-muted-dark max-w-sm">
            When you upload an Excel workbook via <span className="font-semibold text-ink">Excel Import</span>,
            the run will be recorded here.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => {
          const s = r.summary || {};
          const w = s.warnings || [];
          return (
            <div
              key={r.id}
              data-testid={TEST_IDS.importHistoryRow(r.id)}
              className="cf-card cf-fade-up p-4 md:p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet size={16} className="text-gold-600" />
                    <div className="font-display text-lg text-ink" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                      {r.filename || "unknown.xlsx"}
                    </div>
                    <span className="cf-pill num" style={{ background: "rgba(18,49,89,0.06)", color: "#123159" }}>
                      {fmtBytes(r.size_bytes)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center flex-wrap gap-4 text-[11px] text-muted-dark num">
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {fmtDateTime(r.timestamp)}</span>
                    <span className="inline-flex items-center gap-1"><User size={11} /> {r.user_email || "system"}</span>
                    <span>Horizon {r.horizon}m · as-of {r.as_of}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {[
                  ["Projects", s.projects],
                  ["Sales", s.sales],
                  ["Milestones", s.milestones],
                  ["Land fees", s.land_fees],
                  ["Ongoing baselines", s.ongoing_snapshots],
                  ["Flags", s.flags_generated],
                ].map(([label, val]) => (
                  <div key={label} className="bg-paper rounded-lg p-2.5">
                    <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">{label}</div>
                    <div className="num text-lg text-ink font-bold mt-0.5">{val ?? 0}</div>
                  </div>
                ))}
              </div>

              {w.length > 0 && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200">
                  <AlertCircle size={13} className="text-orange-600 mt-0.5 shrink-0" />
                  <div className="text-[11px] text-orange-800 leading-snug">
                    <span className="font-semibold">{w.length} warning{w.length === 1 ? "" : "s"}:</span>{" "}
                    {w.slice(0, 3).join(" · ")}{w.length > 3 ? ` · +${w.length - 3} more` : ""}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </PageShell>
  );
}
